import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Team } from '@ninety/platform/models/_shared/team';
import { SegmentContactTraits } from '@ninety/platform/models/analytics/segment/segment-contact-traits';
import { SegmentGroupTraits } from '@ninety/platform/models/analytics/segment/segment-group-traits';
import { HelperService } from '@ninety/platform/services/services/helper.service';
import { RoleService } from '@ninety/platform/services/services/role.service';

import { selectCurrentUser } from '../../../app-entities/users/users-state.selectors';
import { selectSegmentStripeTraitsModel } from '../../billing/billing-state.selectors';
import { selectCompany } from '../../company/company-state.selectors';
import { selectCurrentPerson } from '../../current-person/current-person.selectors';
import { GainsightAccountAttributes } from '../gainsight/models/gainsight-account-attributes';
import { GainsightUserAttributes } from '../gainsight/models/gainsight-user-attributes';

export const selectSegmentContactTraits: MemoizedSelector<unknown, SegmentContactTraits> = createSelector(
  selectCurrentUser,
  selectCurrentPerson,
  selectCompany,
  selectSegmentStripeTraitsModel,
  (user, person, company, stripe): SegmentContactTraits => {
    const gainsight: GainsightUserAttributes = {
      accountId: company._id,
      active: user.active,
      // This method is only called after company user is fetched, assume true
      completedSignup: true,
      firstVisitDate: HelperService.getCreatedDate(user._id),
      guideEnabled: user.guideEnabled ?? false,
      isLeadership: user.teams.some((t: Pick<Team, 'teamId'>) => t?.teamId === company.seniorLeadershipTeamId) ?? false,
      personIsImplementer: person.isImplementer,
      role: RoleService.roleName(user.roleCode),
      roleCode: user.roleCode,
      signUpDate: HelperService.getCreatedDate(user._id),
      userIsImplementer: user.isImplementer,
    };

    return {
      // Reserved traits
      id: person._id, // Don't use user._id
      email: person.primaryEmail ?? undefined,
      phone: person.primaryPhoneNumber ?? undefined,
      firstName: user.metadata?.name?.first,
      lastName: user.metadata?.name?.last,
      company: {
        id: user.company.companyId,
        name: company.name,
      },

      // Common custom traits
      active: user.active ?? true,
      userId: user._id,
      lastAccessed: new Date().toISOString(),
      bos: company.bos,
      ...gainsight,
      ...stripe,
    };
  }
);

export const selectSegmentGroupTraits: MemoizedSelector<unknown, SegmentGroupTraits> = createSelector(
  selectCompany,
  selectSegmentStripeTraitsModel,
  (company, stripe): SegmentGroupTraits => {
    const gainsight: GainsightAccountAttributes = {
      affiliateCode: company.affiliateCode,
      bos: company.bos,
      clearbitEmployeeRange: company.clearbitEmployeeRange,
      clearbitIndustry: company.clearbitIndustry,
      implementerCode: company.implementerCode,
      implementerFree: company.implementerFree,
      parentAffiliateCode: company.parentAffiliateCode,
      partnerKey: company.partnerKey,
      trialEndDate: company.subscription.trialEnd,
      stripeStatus: company.subscription?.providerStatus,
      subscriptionProvider: company.subscription?.provider,
      accountStatus: company.accountStatus,
      subscriptionPlanId: company.subscription?.planId,
      subscriptionPlanPriceId: company.subscription?.planPriceId,
      subscriptionCancelDate: company.subscription?.willCancelAt,
      subscriptionCanceledDate: company.subscription?.cancelledAt,
      payingLicense: company.subscription?.seatsPaidFor,
      subscriptionType: company.subscription?.type,
      subscriptionSubtype: company.subscription?.subType,
      subscriptionConfigurationId: company.subscription?.subscriptionConfigurationId,

      companyWebsite: company.companyWebsite,
      partnerProgram: company.partnerProgram,
      otherPartnerProgram: company.otherPartnerProgram,

      industry: company.industry?.industry,
      subIndustry: company.industry?.subIndustry,
      industrySignUpOption: company.industry?.signUpOption,
    };

    return {
      avatar: company.logo?.url,
      id: company._id,
      name: company.name,
      phone: company.phoneNumber,
      ...stripe,
      ...gainsight,
    };
  }
);
